import {
  FATORES_DE_ALOCACAO,
  type SalvarPesosDeAlocacao,
  STATUS_DE_RESERVA,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet, apiPost, apiRequest, errorMessage } from '@/lib/api'
import { ROTAS_DE_ALOCACOES } from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

/**
 * Agenda da frota — o calendário (Gantt) e os pesos do scoring de alocação.
 *
 * ---------------------------------------------------------------------------
 * A REGRA DE OURO, NO CALENDÁRIO
 * ---------------------------------------------------------------------------
 * O Gantt é a ÚNICA leitura do painel em que o veículo físico é protagonista —
 * e mesmo assim só porque a alocação já aconteceu. A raia "a alocar" embaixo é
 * o lembrete da regra: a reserva aprovada ocupa capacidade da CATEGORIA sem
 * placa nenhuma, e é o algoritmo (ou o gestor, com justificativa) que decide o
 * carro depois.
 *
 * ---------------------------------------------------------------------------
 * O QUE VEM DE ONDE
 * ---------------------------------------------------------------------------
 * O modelo de LEITURA é declarado aqui (Zod local), espelhando `ganttDaApiSchema`
 * e `pesosDaApiSchema` de `apps/api/src/alocacoes/alocacoes.dto.ts`. Os enums e
 * as constantes — fatores, status de reserva, teto de peso, janela máxima — vêm
 * sempre de `@locaveri/shared`: redigitados aqui seriam divergência esperando o
 * dia em que o motor ganhar um fator.
 *
 * `motivo` do trecho e `statusAtual` do veículo chegam como STRING solta de
 * propósito (é assim no DTO): um motivo novo do lado da API não pode derrubar o
 * calendário inteiro com INVALID_RESPONSE — a tela mostra o texto cru e segue.
 */

// ---------------------------------------------------------------------------
// Gantt — leitura
// ---------------------------------------------------------------------------

/**
 * Um trecho ocupado da agenda de um veículo.
 *
 * `reserva` preenchida é o que separa a barra amarela (ALOCACAO/RESERVA, que
 * abre o detalhe da reserva) da barra cinza (manutenção, limpeza, vistoria…):
 * a decisão visual do Gantt parte deste campo, não do texto do motivo.
 */
export const trechoDoGanttSchema = z.object({
  inicio: z.string(),
  fim: z.string(),
  /** `motivo` do bloqueio (ALOCACAO, MANUTENCAO…); o rótulo é da tela. */
  motivo: z.string(),
  reserva: z
    .object({
      id: z.uuid(),
      numero: z.number().int(),
      status: z.enum(STATUS_DE_RESERVA),
      contaNome: z.string(),
    })
    .nullable(),
})

export type TrechoDoGantt = z.infer<typeof trechoDoGanttSchema>

export const linhaDoGanttSchema = z.object({
  vehicleId: z.uuid(),
  placa: z.string(),
  modelo: z.string(),
  categoriaNome: z.string(),
  statusAtual: z.string(),
  trechos: z.array(trechoDoGanttSchema),
})

export type LinhaDoGantt = z.infer<typeof linhaDoGanttSchema>

/** Reserva aprovada ocupando capacidade sem carro — a raia "a alocar". */
export const reservaSemVeiculoSchema = z.object({
  id: z.uuid(),
  numero: z.number().int(),
  status: z.enum(STATUS_DE_RESERVA),
  categoriaNome: z.string(),
  contaNome: z.string(),
  retirada: z.string(),
  devolucao: z.string(),
})

export type ReservaSemVeiculo = z.infer<typeof reservaSemVeiculoSchema>

export const ganttDaFrotaSchema = z.object({
  de: z.string(),
  ate: z.string(),
  veiculos: z.array(linhaDoGanttSchema),
  naoAlocadas: z.array(reservaSemVeiculoSchema),
})

export type GanttDaFrota = z.infer<typeof ganttDaFrotaSchema>

// ---------------------------------------------------------------------------
// Pesos — leitura
// ---------------------------------------------------------------------------

/**
 * Os pesos vigentes do scoring.
 *
 * `padrao: true` significa que o tenant NUNCA salvou — está valendo
 * `PESOS_PADRAO` do código, e a tela precisa dizer isso em voz alta: um gestor
 * que lê "rodízio: 30" achando que alguém da casa escolheu está tomando decisão
 * de frota sobre um valor de fábrica. Mesmo desenho de `definida` na política
 * de locação.
 */
export const pesosDeAlocacaoLidosSchema = z.object({
  pesos: z.record(z.enum(FATORES_DE_ALOCACAO), z.number().int()),
  padrao: z.boolean(),
})

export type PesosDeAlocacaoLidos = z.infer<typeof pesosDeAlocacaoLidosSchema>

// ---------------------------------------------------------------------------
// Chaves de cache
// ---------------------------------------------------------------------------

export const CHAVE_DO_GANTT = 'gantt-da-frota'
export const CHAVE_DE_PESOS_DE_ALOCACAO = 'pesos-de-alocacao'

// ---------------------------------------------------------------------------
// Gantt
// ---------------------------------------------------------------------------

export interface ParametrosDoGantt {
  /** Instante ISO com fuso explícito. Vazio desliga a consulta. */
  readonly de: string
  readonly ate: string
  /** Vazio = todas as categorias. */
  readonly categoryId: string
}

export function useGantt(parametros: ParametrosDoGantt): UseQueryResult<GanttDaFrota> {
  return useQuery({
    queryKey: [CHAVE_DO_GANTT, parametros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        de: parametros.de,
        ate: parametros.ate,
        categoryId: parametros.categoryId,
      })

      return apiGet(`${ROTAS_DE_ALOCACOES.gantt}${query}`, ganttDaFrotaSchema, { signal })
    },
    // Janela incompleta é pergunta que ainda não existe — a API responderia 422
    // para quem só está trocando a data.
    enabled: parametros.de.length > 0 && parametros.ate.length > 0,
    // Mantém a janela anterior na tela enquanto a nova chega: sem isso o
    // calendário pisca em branco a cada ◀ ▶.
    placeholderData: (anterior) => anterior,
  })
}

// ---------------------------------------------------------------------------
// Realocação pela agenda
// ---------------------------------------------------------------------------

/** Resposta do `POST /reservas/:id/alocacao` — espelha `alocacaoFeitaSchema` da API. */
export const alocacaoRealocadaSchema = z.object({
  alocacaoId: z.uuid(),
  vehicleId: z.uuid(),
  placa: z.string(),
  modelo: z.string(),
  automatica: z.boolean(),
  score: z.number().int().nullable(),
})

export type AlocacaoRealocada = z.infer<typeof alocacaoRealocadaSchema>

export type EtapaDaRealocacao = 'desfazer' | 'alocar-com-retorno' | 'alocar-sem-retorno'

/** Justificativa fixa do retorno automático — vai para a trilha de auditoria. */
const JUSTIFICATIVA_DO_RETORNO = 'Retorno automático ao veículo original após falha na realocação.'

/**
 * Falha da realocação COM o desfecho em que ela aconteceu.
 *
 * A distinção não é cosmética — cada etapa deixa o mundo num estado diferente:
 * - `desfazer`: nada mudou no servidor; o diálogo pode corrigir e tentar de novo.
 * - `alocar-com-retorno`: o destino recusou a reserva, mas o retorno automático
 *   devolveu o veículo ORIGINAL — a reserva NÃO ficou sem carro e o estado é o
 *   mesmo de antes do gesto.
 * - `alocar-sem-retorno`: o destino recusou E o slot original foi tomado no
 *   meio-tempo — a reserva está na raia "a alocar", e a tela precisa dizer
 *   exatamente isso, não um "algo saiu errado" que esconderia o estado
 *   intermediário.
 *
 * `causa` é sempre a falha da alocação no DESTINO (ou do desfazer) — nunca a
 * do retorno, que é só o remendo.
 */
export class ErroDeRealocacao extends Error {
  override readonly name: string = 'ErroDeRealocacao'
  readonly etapa: EtapaDaRealocacao
  readonly causa: unknown

  constructor(etapa: EtapaDaRealocacao, causa: unknown) {
    super(errorMessage(causa))
    this.etapa = etapa
    this.causa = causa
  }

  /** `true` quando o retorno automático manteve o carro original na reserva. */
  get retornouAoOriginal(): boolean {
    return this.etapa === 'alocar-com-retorno'
  }
}

export interface RealocarEntrada {
  readonly reservaId: string
  /** O veículo de destino. É escolha MANUAL — por isso a justificativa. */
  readonly vehicleId: string
  /** O veículo ORIGINAL — o alvo do retorno automático se o destino falhar. */
  readonly vehicleIdOriginal: string
  readonly justificativa: string
  /** Vai para a trilha da alocação desfeita (`POST …/alocacao/desfazer`). */
  readonly motivoDoDesfazimento: string
}

/**
 * Move a reserva de um veículo para outro: desfazer + alocar, NESTA ordem.
 *
 * Não existe endpoint de "mover" — realocar é passar pela máquina duas vezes,
 * com as duas pontas na trilha de auditoria (motivo do desfazimento, depois
 * justificativa da escolha manual). Se o ALOCAR no destino falhar, o hook tenta
 * sozinho devolver o veículo ORIGINAL (com justificativa fixa, para a trilha):
 * deu certo, a reserva não ficou sem carro; o slot original foi tomado no
 * meio-tempo, ela cai mesmo na raia "a alocar" — `ErroDeRealocacao.etapa`
 * distingue os desfechos para o diálogo dizer a verdade.
 *
 * A invalidação fica no `onSettled` de propósito: até o ERRO muda o mundo aqui
 * — um 409 significa que a agenda mudou embaixo do painel, e qualquer falha
 * depois do desfazer deixou a reserva num carro diferente do que a tela mostra
 * (o original de volta, ou nenhum); nos dois casos o calendário está velho.
 */
export function useRealocar(): UseMutationResult<AlocacaoRealocada, Error, RealocarEntrada> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entrada: RealocarEntrada) => {
      try {
        await apiPost(ROTAS_DE_ALOCACOES.desfazer(entrada.reservaId), z.unknown(), {
          motivo: entrada.motivoDoDesfazimento,
        })
      } catch (causa) {
        throw new ErroDeRealocacao('desfazer', causa)
      }

      try {
        return await apiPost(
          ROTAS_DE_ALOCACOES.alocar(entrada.reservaId),
          alocacaoRealocadaSchema,
          { vehicleId: entrada.vehicleId, justificativa: entrada.justificativa },
        )
      } catch (causa) {
        // O destino recusou e a alocação antiga já foi desfeita. Antes de
        // entregar a reserva à raia "a alocar", tenta devolver o carro
        // original — se ninguém tomou o slot, o mundo volta ao estado de
        // antes do gesto.
        let retornou = false

        try {
          await apiPost(ROTAS_DE_ALOCACOES.alocar(entrada.reservaId), alocacaoRealocadaSchema, {
            vehicleId: entrada.vehicleIdOriginal,
            justificativa: JUSTIFICATIVA_DO_RETORNO,
          })
          retornou = true
        } catch {
          // Slot original tomado no meio-tempo — a reserva fica sem carro.
        }

        throw new ErroDeRealocacao(retornou ? 'alocar-com-retorno' : 'alocar-sem-retorno', causa)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DO_GANTT] })
      // A raiz das chaves de `features/reservas` — lista, detalhe, fila e
      // candidatos ficam velhos quando o carro troca de reserva.
      void queryClient.invalidateQueries({ queryKey: ['reservas'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Pesos
// ---------------------------------------------------------------------------

export function usePesos(): UseQueryResult<PesosDeAlocacaoLidos> {
  return useQuery({
    queryKey: [CHAVE_DE_PESOS_DE_ALOCACAO],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_ALOCACOES.pesos, pesosDeAlocacaoLidosSchema, { signal }),
  })
}

/**
 * Salva os pesos (upsert — uma linha por tenant, daí o `PUT`), todos de uma
 * vez: o critério de alocação é propriedade do CONJUNTO, como as tarifas.
 *
 * O corpo é a SAÍDA de `salvarPesosDeAlocacaoSchema` — ao contrário dos
 * formulários de dinheiro, este schema só coage string para inteiro (nada de
 * `bigint` nem `Date`), então `validado.data` é JSON-serializável e é o que
 * viaja.
 *
 * Só a própria leitura sai do cache: a lista de candidatos pontuados vive na
 * feature de reservas (com chave de lá) e é consultada sob demanda ao abrir o
 * detalhe — a pontuação é calculada pelo servidor a cada pedido, com os pesos
 * que valem NAQUELE momento, então não há número velho a expulsar daqui. O
 * Gantt não muda: peso decide QUAL carro, não SE há carro.
 */
export function useSalvarPesos(): UseMutationResult<
  PesosDeAlocacaoLidos,
  Error,
  SalvarPesosDeAlocacao
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: SalvarPesosDeAlocacao) =>
      apiRequest(ROTAS_DE_ALOCACOES.pesos, {
        schema: pesosDeAlocacaoLidosSchema,
        method: 'PUT',
        body: dados,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_PESOS_DE_ALOCACAO] })
    },
  })
}
